import { SEO } from "@/components/SEO";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Tag, ArrowRight } from "lucide-react";
import { BUSINESS } from "@/data/businessInfo";
import { ServiceCardsSection, LocationCardsSection } from "@/components/SharedSections";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

export default function BlogPage() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/blog`).then(r => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <SEO title="Blog - Electrical Tips & Resources" description="Helpful articles about electrical services, safety tips, and home improvement for Treasure Coast homeowners and businesses." path="/blog" />
      <section className="bg-[#FAFAFA] py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#BF8D30] mb-3 block" style={{ fontFamily: 'Outfit, sans-serif' }}>Blog</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#401303] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Resources & Insights</h1>
            <p className="mt-4 text-base text-[#6B3A1F] max-w-2xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>Helpful articles about electrical services, safety tips, and Treasure Coast home improvement.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {posts.map((post, i) => (
              <motion.div key={post.id || i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.3, delay: i * 0.05 }} className="bg-white rounded-2xl border border-[#6B3A1F]/10 overflow-hidden hover:shadow-lg transition-shadow duration-300" data-testid={`blog-post-${i}`}>
                <Link to={`/blog/${post.slug}`} className="block p-6 group">
                  <div className="flex items-center gap-3 mb-3">
                    {post.category && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#D97904]/10 rounded-full text-xs font-semibold text-[#6B3A1F]">
                        <Tag className="w-3 h-3" /> {post.category}
                      </span>
                    )}
                    {post.read_time && (
                      <span className="flex items-center gap-1 text-xs text-[#6B3A1F]">
                        <Clock className="w-3 h-3" /> {post.read_time}
                      </span>
                    )}
                  </div>
                  <h2 className="text-lg font-bold text-[#401303] mb-2 leading-tight group-hover:text-[#D97904] transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }}>{post.title}</h2>
                  <p className="text-sm text-[#6B3A1F] leading-relaxed mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-[#6B3A1F]/60">{post.date}</p>
                    <span className="text-xs font-semibold text-[#D97904] flex items-center gap-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Read Article <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200" /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {posts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#6B3A1F]" style={{ fontFamily: 'Manrope, sans-serif' }}>Loading blog posts...</p>
            </div>
          )}

          <div className="mt-12 text-center">
            <p className="text-sm text-[#6B3A1F] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>More articles coming soon. Check back regularly for electrical tips and Treasure Coast insights.</p>
          </div>
        </div>
      </section>

      <ServiceCardsSection />
      <LocationCardsSection />
    </div>
  );
}
